import cookies from "js-cookie";

import { getDocument, getWindow } from "@thelabnyc/thelabui/src/utils/dom";

/**
 * Name of CSRF Token Header
 */
export const CSRF_HEADER = "X-CSRFToken";

/**
 * Get the current locale from the document's lang attribute.
 * Django sets this based on the active locale, so it's the authoritative source.
 * Falls back to "en" if not available (e.g., in SSR context).
 */
export const getCurrentLocale = (): string => {
    const document = getDocument();
    if (!document) {
        return "en";
    }
    return document.documentElement.lang || "en";
};

/**
 * Load a CSRF token using the API. Sometimes necessary since CSRF tokens aren't included in cached page responses.
 */
export const loadCSRFToken = async (): Promise<string> => {
    const resp = await wafAwareFetch("/api/csrf-token/", {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
    });
    const data = (await resp.json()) as { csrf_token: string };
    return data.csrf_token;
};

/**
 * Get the value of the CSRF Token Cookie
 */
export const getCSRFToken = async (): Promise<string> => {
    let token: string | undefined = cookies.get("csrftoken");
    if (!token) {
        token = await loadCSRFToken();
    }
    return token;
};

/**
 * Client-side API exposed by the AWS WAF JavaScript challenge SDK, loaded on
 * every page by each site's PageSkeleton (chrome.aws_waf_url).
 */
interface AwsWafIntegration {
    fetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response>;
}

/**
 * True when the URL targets the current origin (relative URLs included).
 */
const isSameOrigin = (url: string): boolean => {
    const window = getWindow();
    if (!window) {
        return false;
    }
    try {
        return (
            new URL(url, window.location.href).origin === window.location.origin
        );
    } catch {
        return false;
    }
};

/**
 * Fetch through the AWS WAF SDK when it's loaded and the request targets our
 * own origin. The SDK's fetch wrapper acquires a WAF token and attaches it to
 * the request, preventing AWS WAF from answering API calls with its HTML
 * challenge interstitial (served as HTTP 202). Cross-origin requests stay on
 * plain fetch: the token header isn't CORS-safelisted (it would force
 * preflights) and would leak our WAF token to third parties.
 */
const wafAwareFetch = (url: string, init: RequestInit): Promise<Response> => {
    const waf = (globalThis as { AwsWafIntegration?: AwsWafIntegration })
        .AwsWafIntegration;
    return waf && isSameOrigin(url) ? waf.fetch(url, init) : fetch(url, init);
};

/**
 * Custom error class for HTTP errors with response body access
 */
export class HttpError extends Error {
    readonly status: number;
    readonly body: unknown;
    readonly response: { readonly body: unknown; readonly status: number };

    constructor(message: string, status: number, body: unknown) {
        super(message);
        this.name = "HttpError";
        this.status = status;
        this.body = body;
        // For backward compatibility with superagent error pattern
        this.response = { body, status };
    }
}

/**
 * Helper to parse JSON response and throw HttpError on non-OK status
 */
export const fetchJson = async <T>(
    url: string,
    options?: RequestInit,
): Promise<T> => {
    const locale = getCurrentLocale();
    const resp = await wafAwareFetch(url, {
        ...options,
        credentials: options?.credentials ?? "same-origin",
        headers: {
            "Accept": "application/json",
            "Accept-Language": locale,
            ...options?.headers,
        },
    });

    // Handle 204 No Content responses (e.g. successful DELETE operations)
    if (resp.status === 204) {
        return undefined as T;
    }

    const contentType = resp.headers.get("content-type");
    const body = contentType?.includes("application/json")
        ? await resp.json()
        : await resp.text();

    if (!resp.ok) {
        throw new HttpError(
            `HTTP ${resp.status}: ${resp.statusText}`,
            resp.status,
            body,
        );
    }

    // An HTML body on one of our own JSON endpoints means an intermediary
    // intercepted the request — most commonly AWS WAF's challenge
    // interstitial, which is served with a 2xx status (202). Surface it as an
    // HttpError (without embedding the HTML in the message) instead of
    // handing the HTML to callers' io-ts decoders. Cross-origin endpoints
    // keep the legacy text pass-through behavior.
    if (contentType?.includes("text/html") && isSameOrigin(url)) {
        throw new HttpError(
            `Expected JSON response but got ${contentType} (HTTP ${resp.status})`,
            resp.status,
            body,
        );
    }

    return body as T;
};
